import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMessages,
  fetchMessageById,
  answerMessage,
  confirmMessage,
  deleteMessage,
  clearDetail,
} from "@/store/altingross/messages";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Trash2,
  Eye,
  Send,
  MessageSquare,
  Mail,
  Phone,
  User,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

// ─── Sabitler ────────────────────────────────────────────────────────────────
const STATUS = {
  1: { label: "Yeni", color: "bg-blue-100 text-blue-700", icon: Circle },
  2: { label: "İşlemde", color: "bg-yellow-100 text-yellow-700", icon: AlertCircle },
  3: { label: "Yanıtlandı", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
};

const SUBJECT = {
  1: "Toptan Satın Alma",
  2: "Perakende Ortaklık",
  3: "Kurumsal Teklif",
  4: "Fiyat Teklifi",
  5: "Ürün Bilgisi",
  6: "Şikayet / Geri Bildirim",
  7: "Diğer",
};

const PAGE_SIZE = 20;

function StatusBadge({ status }) {
  const s = STATUS[status] ?? { label: "—", color: "bg-gray-100 text-gray-500" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${s.color}`}>
      {s.label}
    </span>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <span className="text-sm text-gray-800 break-words">{value}</span>
    </div>
  );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────
export default function Messages() {
  const dispatch = useDispatch();
  const {
    list, loading, pagination,
    detail, detailLoading,
    answeringId, confirmingId, deletingId,
  } = useSelector((s) => s.messages);

  const [page, setPage] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [responseNote, setResponseNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ─── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(
    (p = page) => dispatch(fetchMessages({ page: p, size: PAGE_SIZE })),
    [dispatch, page]
  );

  useEffect(() => { load(page); }, [page]); // eslint-disable-line

  // ─── Detay aç ──────────────────────────────────────────────────────────────
  const openDetail = (id) => {
    dispatch(fetchMessageById(id));
    setResponseNote("");
    setSheetOpen(true);
  };

  const closeDetail = () => {
    setSheetOpen(false);
    dispatch(clearDetail());
    setResponseNote("");
  };

  // ─── Yanıtla ───────────────────────────────────────────────────────────────
  const handleAnswer = async () => {
    if (!detail || !responseNote.trim()) return;
    const res = await dispatch(answerMessage({ id: detail.id, responseNote: responseNote.trim() }));
    if (!res.error) {
      toast.success("Yanıt kaydedildi.");
      dispatch(fetchMessageById(detail.id));
      load(page);
    } else {
      toast.error("Yanıt kaydedilemedi.");
    }
  };

  // ─── Mail gönder ───────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!detail) return;
    const res = await dispatch(confirmMessage(detail.id));
    if (!res.error) {
      toast.success("Yanıt müşteriye mail olarak gönderildi.");
      dispatch(fetchMessageById(detail.id));
      load(page);
    } else {
      toast.error("Mail gönderilemedi.");
    }
  };

  // ─── Sil ───────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    const res = await dispatch(deleteMessage(deleteTarget.id));
    if (!res.error) {
      toast.success("Mesaj silindi.");
      if (sheetOpen && detail?.id === deleteTarget.id) closeDetail();
      load(page);
    } else {
      toast.error("Silme başarısız.");
    }
    setDeleteTarget(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        testId="messages-header"
        title="Mesajlar"
        description="Müşteri ve ziyaretçi mesajlarını yönetin, yanıtlayın."
      />

      {/* Liste */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Kolon başlıkları */}
        <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center border-b border-gray-100 px-4 py-2.5 gap-4">
          <span className="text-xs font-semibold text-black uppercase tracking-wide">Gönderen</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden md:block">Konu</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden sm:block">Durum</span>
          <span className="text-xs font-semibold text-black uppercase tracking-wide hidden sm:block">Tarih</span>
          <span></span>
        </div>

        {loading && list.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Yükleniyor…
          </div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-slate-400 text-sm">
            Henüz mesaj bulunmuyor.
          </div>
        ) : (
          <ul>
            {list.map((m, idx) => (
              <li
                key={m.id}
                className={`grid grid-cols-[1fr_auto_auto_auto_auto] items-center px-4 py-3 gap-4 ${
                  idx !== list.length - 1 ? "border-b border-gray-100" : ""
                } hover:bg-gray-50 transition-colors ${!m.isRead ? "bg-blue-50/40" : ""}`}
              >
                {/* Gönderen */}
                <span className="flex items-center gap-2 min-w-0">
                  {!m.isRead && (
                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" title="Okunmadı" />
                  )}
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {m.name} {m.surname}
                    </span>
                    <span className="text-xs text-gray-400 truncate">{m.email}</span>
                  </span>
                </span>

                {/* Konu */}
                <span className="text-xs text-gray-500 hidden md:block whitespace-nowrap">
                  {SUBJECT[m.subject] ?? "—"}
                </span>

                {/* Durum */}
                <span className="hidden sm:block">
                  <StatusBadge status={m.status} />
                </span>

                {/* Tarih */}
                <span className="text-xs text-gray-400 hidden sm:block whitespace-nowrap">
                  {m.createdAt ? new Date(m.createdAt).toLocaleDateString("tr-TR") : "—"}
                </span>

                {/* Aksiyonlar */}
                <span className="flex items-center gap-1">
                  {deletingId === m.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-red-400" />
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-400 hover:text-gray-700"
                        onClick={() => openDetail(m.id)}
                        title="Detay"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      {!m.isConfirm && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-red-500"
                          onClick={() => setDeleteTarget(m)}
                          title="Sil"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {/* Sayfalama */}
        {list.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
            <span>
              Toplam {pagination.count} mesaj — Sayfa {pagination.page}/{pagination.totalPages || 1}
            </span>
            <div className="flex gap-1.5">
              <Button
                variant="outline" size="sm" className="h-7 text-xs"
                disabled={!pagination.hasPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Önceki
              </Button>
              <Button
                variant="outline" size="sm" className="h-7 text-xs"
                disabled={!pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Detay Sheet ──────────────────────────────────────────────────────── */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col gap-0 p-0 overflow-hidden">
          <SheetHeader className="flex-shrink-0 px-6 py-4 border-b border-gray-100">
            <SheetTitle className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-violet-500" />
              Mesaj Detayı
            </SheetTitle>
            <SheetDescription className="text-xs">
              {detail ? `${detail.name} ${detail.surname} tarafından gönderildi` : "Yükleniyor…"}
            </SheetDescription>
          </SheetHeader>

          {detailLoading ? (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Yükleniyor…
            </div>
          ) : detail ? (
            <div className="flex-1 overflow-y-auto flex flex-col gap-0">

              {/* Kişi bilgileri */}
              <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span>{detail.name} {detail.surname}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span>{detail.phone || "—"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700 col-span-2">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="break-all">{detail.email}</span>
                  </div>
                  {detail.guestId && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 col-span-2 mt-1 bg-gray-50 p-2 rounded">
                      <span className="font-semibold text-gray-400 uppercase tracking-wider">Misafir ID:</span>
                      <span className="font-mono">{detail.guestId}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <StatusBadge status={detail.status} />
                  <span className="text-xs text-gray-500">
                    {SUBJECT[detail.subject] ?? "—"}
                  </span>
                  {detail.isRead && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Okundu
                    </span>
                  )}
                </div>
              </div>

              {/* Mesaj içeriği */}
              <div className="px-6 py-4 border-b border-gray-100 flex flex-col gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Mesaj</span>
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {detail.content}
                </p>
                <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                  <Clock className="w-3 h-3" />
                  {detail.createdAt
                    ? new Date(detail.createdAt).toLocaleString("tr-TR")
                    : "—"}
                </div>
              </div>

              {/* Mevcut yanıt varsa göster */}
              {detail.responseNote && (
                <div className={`px-6 py-4 border-b border-gray-100 flex flex-col gap-2 ${detail.isConfirm ? "bg-blue-50" : "bg-green-50"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-[11px] font-semibold uppercase tracking-wide ${detail.isConfirm ? "text-blue-600" : "text-green-600"}`}>
                      Mevcut Yanıt
                    </span>
                    {detail.isConfirm && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" />
                        Onaylandı · Düzenlenemez
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                    {detail.responseNote}
                  </p>
                  {detail.answeredAt && (
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Clock className="w-3 h-3" />
                      {new Date(detail.answeredAt).toLocaleString("tr-TR")}
                    </div>
                  )}
                </div>
              )}

              {/* Yanıt formu */}
              <div className="px-6 py-4 flex flex-col gap-3">
                {detail.isConfirm ? (
                  <>
                    <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      Bu mesajın yanıtı onaylanmıştır. Yanıt güncellenemez ancak tekrar mail gönderilebilir.
                    </div>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white w-full"
                      disabled={confirmingId === detail.id}
                      onClick={handleConfirm}
                    >
                      {confirmingId === detail.id
                        ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Gönderiliyor…</>
                        : <><Send className="w-4 h-4 mr-1" />Yanıtı Tekrar Gönder</>}
                    </Button>
                  </>
                ) : (
                  <>
                    <Label className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {detail.responseNote ? "Yanıtı Güncelle" : "Yanıt Yaz"}
                    </Label>
                    <Textarea
                      rows={5}
                      placeholder="Müşteriye iletmek istediğiniz yanıtı yazın…"
                      value={responseNote}
                      onChange={(e) => setResponseNote(e.target.value)}
                      className="resize-none text-sm"
                    />

                    <div className="flex gap-2">
                      {/* Kaydet */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        disabled={!responseNote.trim() || answeringId === detail.id}
                        onClick={handleAnswer}
                      >
                        {answeringId === detail.id
                          ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Kaydediliyor…</>
                          : <><MessageSquare className="w-4 h-4 mr-1" />Kaydet</>}
                      </Button>

                      {/* Kaydet & Mail Gönder */}
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        disabled={
                          !responseNote.trim() ||
                          confirmingId === detail.id ||
                          answeringId === detail.id
                        }
                        onClick={async () => {
                          if (!responseNote.trim()) return;
                          const saveRes = await dispatch(
                            answerMessage({ id: detail.id, responseNote: responseNote.trim() })
                          );
                          if (!saveRes.error) {
                            handleConfirm();
                          } else {
                            toast.error("Yanıt kaydedilemedi, mail gönderilmedi.");
                          }
                        }}
                      >
                        {confirmingId === detail.id
                          ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Gönderiliyor…</>
                          : <><Send className="w-4 h-4 mr-1" />Kaydet & Mail Gönder</>}
                      </Button>
                    </div>

                    {/* Sadece onay — mevcut yanıt varsa */}
                    {detail.responseNote && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 w-full"
                        disabled={confirmingId === detail.id}
                        onClick={handleConfirm}
                      >
                        {confirmingId === detail.id
                          ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Gönderiliyor…</>
                          : <><Send className="w-4 h-4 mr-1" />Mevcut yanıtı müşteriye gönder</>}
                      </Button>
                    )}
                  </>
                )}
              </div>

              {/* Sil butonu — isConfirm ise gizle */}
              {!detail.isConfirm && (
              <div className="px-6 py-3 border-t border-gray-100 mt-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-500 hover:text-red-600 hover:bg-red-50 w-full"
                  onClick={() => setDeleteTarget(detail)}
                  disabled={deletingId === detail.id}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Mesajı Sil
                </Button>
              </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* ─── Sil Onayı ────────────────────────────────────────────────────────── */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mesajı sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name} {deleteTarget?.surname}</strong> kişisinin mesajı kalıcı olarak silinecek. Emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={handleDelete}>
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
